import { useState } from 'react';

import { post } from '../../api/api';

import { ndjsonParser } from '../../api/util/ndjson-parser';
import { LanguageCode } from '../../util/languages';

type UseUnsentMessageTranslationProps = {
  /**
   * Subscribe to unique change events for the messageBody
   */
  onMessageBodyChange: (newMessageBody: string) => any;
  /**
   * Subscribe to unique change events for the subject if provided
   */
  onSubjectChange?: (newSubject: string) => any;
  /**
   * Expose when the loading state changes when not using redux
   */
  onLoading?: (loading: boolean) => void;
  /**
   * Fired off when streaming is done
   */
  onFinished?: () => void;
};

type UseUnsentMessageTranslationResponse = {
  /**
   * Whether or not the consumer should render the translation
   */
  shouldShowTranslation: boolean;
  /**
   * Sets shouldShowTranslation false
   */
  hideTranslation: () => void;
  /**
   * Commences a message translation
   */
  onTranslateMessageClick: (params: {
    messageBody: string;
    languageCode: LanguageCode;
    subject?: string;
  }) => Promise<void>;
  /**
   * Whether or not a translation is actively streaming
   */
  loading?: boolean;
  /**
   * Resets the state of the hook including translations and refs
   */
  reset: () => void;
};

/**
 * Hook that exposes message translations for unsent messages. Exposes fields
 * for use in a TranslateButton
 */
export const useUnsentMessageTranslation = ({
  onMessageBodyChange,
  onSubjectChange,
  onLoading,
  onFinished,
}: UseUnsentMessageTranslationProps): UseUnsentMessageTranslationResponse => {
  const [shouldShowTranslation, setShouldShowTranslation] = useState(false);
  const [loading, setLoading] = useState(false);

  const showTranslation = () => {
    setShouldShowTranslation(true);
  };

  const hideTranslation = () => {
    setShouldShowTranslation(false);
  };

  const reset = () => {
    hideTranslation();
    setLoading(false);
  };

  const handleSetLoading = (isLoading: boolean) => {
    if (onLoading) {
      onLoading(isLoading);
    }
    setLoading(isLoading);
  };

  const onTranslateMessageClick: UseUnsentMessageTranslationResponse['onTranslateMessageClick'] =
    async ({ messageBody, languageCode, subject }) => {
      handleSetLoading(true);
      showTranslation();
      try {
        const response = await post('/message-translation/translate', {
          body: JSON.stringify({
            messageBody,
            subject,
            languageCode,
          } as MessageTranslationRequest),
        });

        const reader = response.body?.getReader();
        let newMessageBody: string | undefined;
        let newSubject: string | undefined;
        await reader?.read().then(async function processStream({ done, value }) {
          ndjsonParser(value).forEach(
            (datum: { key: 'messageBody' | 'subject'; value: string }) => {
              if (!datum.key || !datum.value) {
                throw new Error('Invalid stream format');
              }
              const { key, value: chunk } = datum;
              if (key === 'messageBody') {
                newMessageBody = (newMessageBody ?? '') + chunk;
                onMessageBodyChange(newMessageBody);
              } else if (key === 'subject') {
                newSubject = (newSubject ?? '') + chunk;
                if (onSubjectChange) onSubjectChange(newSubject);
              } else {
                throw new Error('Invalid stream key');
              }
            },
          );

          if (!done) {
            await reader?.read().then(processStream);
          }
        });
      } finally {
        if (onFinished) onFinished();
        handleSetLoading(false);
      }
    };

  return {
    shouldShowTranslation,
    hideTranslation,
    onTranslateMessageClick,
    loading,
    reset,
  };
};
